import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ArrowLeft, Loader2, Plus, Save, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";

const WEEKS = 13;
const gbp = (n) => (n === null || n === undefined ? "—" : `${n < 0 ? "-" : ""}£${Math.abs(Math.round(n)).toLocaleString("en-GB")}`);
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); } catch { return d; } };

function nextMonday() {
    const d = new Date();
    d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7));
    return d.toISOString().slice(0, 10);
}
const blankLine = (kind) => ({ id: crypto.randomUUID(), label: "", kind, amounts: Array(WEEKS).fill("") });
const toNum = (v) => { const n = Number(String(v ?? "").replace(/[£,\s]/g, "")); return Number.isFinite(n) ? n : 0; };

// Mirrors backend/utils/cashflow_engine.compute for instant feedback; the server result is authoritative on save.
function computeLocal(openingCash, facility, lines, start) {
    let bal = toNum(openingCash);
    const fac = toNum(facility);
    const weeks = [];
    for (let w = 0; w < WEEKS; w++) {
        const receipts = lines.filter(l => l.kind === "receipt").reduce((s, l) => s + toNum(l.amounts[w]), 0);
        const payments = lines.filter(l => l.kind === "payment").reduce((s, l) => s + toNum(l.amounts[w]), 0);
        const opening = bal;
        bal = opening + receipts - payments;
        const d = new Date(start); d.setDate(d.getDate() + 7 * w);
        weeks.push({ week: w + 1, week_start: d.toISOString().slice(0, 10), opening, receipts, payments, closing: bal, headroom: bal + fac });
    }
    const low = weeks.reduce((a, b) => (b.closing < a.closing ? b : a), weeks[0]);
    return {
        weeks,
        summary: {
            lowest_closing: low.closing, lowest_week: low.week,
            first_negative_week: weeks.find(w => w.closing < 0)?.week ?? null,
            first_facility_breach_week: weeks.find(w => w.closing + fac < 0)?.week ?? null,
            peak_funding_need: Math.max(0, -(low.closing + fac)),
        },
    };
}

function Stat({ label, value, tone, sub }) {
    const cls = tone === "bad" ? "text-red-700" : tone === "warn" ? "text-amber-700" : "text-[#0B1F3A]";
    return (
        <div className="border border-[#E2E8F0] rounded-md bg-white px-4 py-3 min-w-[170px]">
            <p className="text-[11px] uppercase tracking-wider text-[#94A3B8]">{label}</p>
            <p className={`text-xl font-semibold ${cls}`}>{value}</p>
            {sub && <p className="text-[11px] text-[#64748B]">{sub}</p>}
        </div>
    );
}

function Bars({ weeks }) {
    const max = Math.max(1, ...weeks.map(w => Math.abs(w.closing)));
    return (
        <div className="flex items-center gap-1 h-24" aria-label="Closing cash by week" data-testid="cf-chart">
            {weeks.map(w => {
                const h = Math.max(2, (Math.abs(w.closing) / max) * 44);
                const neg = w.closing < 0;
                return (
                    <div key={w.week} className="flex-1 flex flex-col items-center h-full" title={`Week ${w.week}: ${gbp(w.closing)}`}>
                        <div className="flex-1 flex items-end w-full justify-center">{!neg && <div className="w-full rounded-t bg-[#0B1F3A]" style={{ height: h }} />}</div>
                        <div className="w-full h-px bg-[#CBD5E1]" />
                        <div className="flex-1 flex items-start w-full justify-center">{neg && <div className="w-full rounded-b bg-red-500" style={{ height: h }} />}</div>
                    </div>
                );
            })}
        </div>
    );
}

function List() {
    const [params] = useSearchParams();
    const mandate = params.get("mandate");
    const [rows, setRows] = useState(null);
    useEffect(() => {
        api.get("/distressed/cashflow", { params: mandate ? { mandate_id: mandate } : {} })
            .then(r => setRows(r.data.forecasts)).catch(() => setRows([]));
    }, [mandate]);
    const q = mandate ? `?mandate=${mandate}` : "";
    return (
        <div className="w-full">
            <PageHeader title="Cash flow forecasts" subtitle="13-week direct cash flow: when does the cash run out?">
                <Link to={`/distressed/cashflow/new${q}`}><Button size="sm" data-testid="cf-new"><Plus className="w-3.5 h-3.5 mr-1.5" />New forecast</Button></Link>
            </PageHeader>
            <div className="px-6 pb-8 w-full">
                {rows === null ? <Loader2 className="w-4 h-4 animate-spin text-[#64748B]" /> : rows.length === 0 ? (
                    <p className="text-[13px] text-[#64748B]">No forecasts yet.</p>
                ) : (
                    <table className="w-full text-[13px]">
                        <thead className="text-[11px] uppercase tracking-wider text-[#94A3B8]"><tr>
                            <th className="text-left py-2">Forecast</th><th className="text-left">Starts</th>
                            <th className="text-right">Lowest cash</th><th className="text-right">Cash negative</th><th className="text-right">Funding gap</th></tr></thead>
                        <tbody>{rows.map(r => (
                            <tr key={r.id} className="border-t border-[#F1F5F9] hover:bg-[#F8FAFC]">
                                <td className="py-2"><Link to={`/distressed/cashflow/${r.id}`} className="font-medium text-[#0B1F3A] hover:underline">{r.name}</Link></td>
                                <td>{fmtDate(r.start_date)}</td>
                                <td className="text-right tabular-nums">{gbp(r.summary.lowest_closing)} <span className="text-[#94A3B8]">(wk {r.summary.lowest_week})</span></td>
                                <td className="text-right">{r.summary.first_negative_week ? `Week ${r.summary.first_negative_week}` : "—"}</td>
                                <td className="text-right tabular-nums">{r.summary.peak_funding_need > 0 ? <span className="text-red-700">{gbp(r.summary.peak_funding_need)}</span> : "None"}</td>
                            </tr>))}</tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function Editor({ id }) {
    const nav = useNavigate();
    const [params] = useSearchParams();
    const isNew = id === "new";
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const [f, setF] = useState({
        name: "", start_date: nextMonday(), opening_cash: "", facility_limit: "",
        company_number: params.get("company") || "", mandate_id: params.get("mandate") || null, notes: "",
    });
    const [lines, setLines] = useState([blankLine("receipt"), blankLine("payment")]);
    const set = (k, v) => setF(x => ({ ...x, [k]: v }));

    useEffect(() => {
        if (isNew) return;
        api.get(`/distressed/cashflow/${id}`).then(({ data }) => {
            setF({ name: data.name, start_date: data.start_date, opening_cash: String(data.opening_cash ?? ""),
                facility_limit: data.facility_limit == null ? "" : String(data.facility_limit),
                company_number: data.company_number || "", mandate_id: data.mandate_id || null, notes: data.notes || "" });
            setLines(data.lines.map(l => ({ ...l, amounts: l.amounts.map(a => (a === 0 ? "" : String(a))) })));
        }).catch(err => toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not load")).finally(() => setLoading(false));
    }, [id, isNew]);

    const calc = useMemo(() => computeLocal(f.opening_cash, f.facility_limit, lines, f.start_date), [f.opening_cash, f.facility_limit, lines, f.start_date]);
    const s = calc.summary;

    const setCell = (li, w, v) => setLines(ls => ls.map((l, i) => (i === li ? { ...l, amounts: l.amounts.map((a, j) => (j === w ? v : a)) } : l)));
    const setLine = (li, patch) => setLines(ls => ls.map((l, i) => (i === li ? { ...l, ...patch } : l)));

    const seed = async () => {
        if (!f.company_number) return toast.error("Enter a company number first");
        setSeeding(true);
        try {
            const { data } = await api.get(`/distressed/cashflow/seed/${f.company_number.trim()}`, { params: { start_date: f.start_date } });
            if (data.opening_cash != null) set("opening_cash", String(data.opening_cash));
            if (data.lines.length) setLines(ls => [...ls.filter(l => l.label || l.amounts.some(a => a !== "")), ...data.lines.map(l => ({ ...l, id: crypto.randomUUID(), amounts: l.amounts.map(a => (a === 0 ? "" : String(a))) }))]);
            toast.message(data.note);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not seed");
        } finally { setSeeding(false); }
    };

    const save = async () => {
        setSaving(true);
        try {
            const body = {
                name: f.name, start_date: f.start_date, opening_cash: toNum(f.opening_cash),
                facility_limit: f.facility_limit === "" ? null : toNum(f.facility_limit),
                company_number: f.company_number || null, mandate_id: f.mandate_id, notes: f.notes || null,
                lines: lines.filter(l => l.label.trim()).map(l => ({ id: l.id, label: l.label, kind: l.kind, category: l.category || null, amounts: l.amounts.map(toNum) })),
            };
            const { data } = isNew ? await api.post("/distressed/cashflow", body) : await api.put(`/distressed/cashflow/${id}`, body);
            toast.success("Forecast saved");
            if (isNew) nav(`/distressed/cashflow/${data.id}`, { replace: true });
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
        } finally { setSaving(false); }
    };

    const remove = async () => {
        try { await api.delete(`/distressed/cashflow/${id}`); nav("/distressed/cashflow"); } catch { toast.error("Delete failed"); }
    };

    if (loading) return <div className="p-6"><Loader2 className="w-4 h-4 animate-spin" /></div>;
    const back = f.mandate_id ? `/distressed/mandates/${f.mandate_id}` : "/distressed/cashflow";
    const cell = "h-8 w-[84px] px-1.5 text-right text-[12px] tabular-nums";

    return (
        <div className="w-full" data-testid="cf-editor">
            <PageHeader title={f.name || "New cash flow forecast"} subtitle="13-week direct cash flow">
                <Link to={back}><Button variant="outline" size="sm"><ArrowLeft className="w-3.5 h-3.5 mr-1.5" />Back</Button></Link>
                {!isNew && <Button variant="outline" size="sm" onClick={remove}><Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete</Button>}
                <Button size="sm" onClick={save} disabled={saving || !f.name.trim()} data-testid="cf-save">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}Save
                </Button>
            </PageHeader>

            <div className="px-6 pb-8 space-y-5 w-full">
                <Card><CardContent className="pt-5 grid grid-cols-2 md:grid-cols-6 gap-3">
                    <label className="text-[12px] text-[#475569] md:col-span-2">Name<Input value={f.name} onChange={e => set("name", e.target.value)} data-testid="cf-name" /></label>
                    <label className="text-[12px] text-[#475569]">Week 1 starts<Input type="date" value={f.start_date} onChange={e => set("start_date", e.target.value)} /></label>
                    <label className="text-[12px] text-[#475569]">Opening cash (£)<Input inputMode="decimal" value={f.opening_cash} onChange={e => set("opening_cash", e.target.value)} data-testid="cf-opening" /></label>
                    <label className="text-[12px] text-[#475569]">Facility available (£)<Input inputMode="decimal" value={f.facility_limit} onChange={e => set("facility_limit", e.target.value)} placeholder="optional" /></label>
                    <div className="text-[12px] text-[#475569]">Company number
                        <div className="flex gap-1"><Input value={f.company_number} onChange={e => set("company_number", e.target.value)} placeholder="optional" />
                            <Button variant="outline" size="sm" onClick={seed} disabled={seeding} title="Pre-fill from the latest filed accounts" data-testid="cf-seed">
                                {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}</Button></div>
                    </div>
                </CardContent></Card>

                <div className="flex flex-wrap gap-3" data-testid="cf-summary">
                    <Stat label="Lowest closing cash" value={gbp(s.lowest_closing)} sub={`Week ${s.lowest_week}`} tone={s.lowest_closing < 0 ? "bad" : undefined} />
                    <Stat label="Cash first negative" value={s.first_negative_week ? `Week ${s.first_negative_week}` : "Not in 13 weeks"} tone={s.first_negative_week ? "warn" : undefined} />
                    <Stat label="Facility exceeded" value={s.first_facility_breach_week ? `Week ${s.first_facility_breach_week}` : "No"} tone={s.first_facility_breach_week ? "bad" : undefined} />
                    <Stat label="Funding gap (peak)" value={s.peak_funding_need > 0 ? gbp(s.peak_funding_need) : "None"} sub="Beyond cash and facility" tone={s.peak_funding_need > 0 ? "bad" : undefined} />
                </div>

                <Card><CardHeader className="pb-1"><CardTitle className="text-[14px]">Closing cash by week</CardTitle></CardHeader><CardContent><Bars weeks={calc.weeks} /></CardContent></Card>

                <Card><CardContent className="pt-5 overflow-x-auto">
                    <table className="text-[12px]" data-testid="cf-grid">
                        <thead><tr className="text-[11px] text-[#94A3B8]">
                            <th className="text-left pr-3 min-w-[220px]">Line</th>
                            {calc.weeks.map(w => <th key={w.week} className="px-1 font-normal text-right"><div className="font-medium text-[#475569]">Wk {w.week}</div>{fmtDate(w.week_start)}</th>)}
                            <th /></tr></thead>
                        <tbody>
                            {["receipt", "payment"].map(kind => (
                                <FragmentRows key={kind} kind={kind} lines={lines} setCell={setCell} setLine={setLine} cell={cell}
                                    onAdd={() => setLines(ls => [...ls, blankLine(kind)])} onDelete={(i) => setLines(ls => ls.filter((_, j) => j !== i))} />
                            ))}
                            <tr className="border-t-2 border-[#E2E8F0] font-medium"><td className="pt-2">Net movement</td>{calc.weeks.map(w => <td key={w.week} className="text-right px-1.5 pt-2 tabular-nums">{gbp(w.receipts - w.payments)}</td>)}<td /></tr>
                            <tr className="font-semibold"><td>Closing cash</td>{calc.weeks.map(w => <td key={w.week} className={`text-right px-1.5 tabular-nums ${w.closing < 0 ? "text-red-700" : ""}`}>{gbp(w.closing)}</td>)}<td /></tr>
                            {toNum(f.facility_limit) > 0 && <tr className="text-[#64748B]"><td>Headroom incl. facility</td>{calc.weeks.map(w => <td key={w.week} className={`text-right px-1.5 tabular-nums ${w.headroom < 0 ? "text-red-700" : ""}`}>{gbp(w.headroom)}</td>)}<td /></tr>}
                        </tbody>
                    </table>
                </CardContent></Card>
                <p className="text-[11px] text-[#94A3B8]">Enter all amounts as positive numbers; the section decides whether it is a receipt or a payment. Figures seeded from filed accounts are starting assumptions only.</p>
            </div>
        </div>
    );
}

function FragmentRows({ kind, lines, setCell, setLine, cell, onAdd, onDelete }) {
    const title = kind === "receipt" ? "Receipts" : "Payments";
    return (
        <>
            <tr><td colSpan={WEEKS + 2} className="pt-3 pb-1"><Badge variant="outline" className={kind === "receipt" ? "bg-green-50 text-green-800 border-green-200" : "bg-amber-50 text-amber-800 border-amber-200"}>{title}</Badge></td></tr>
            {lines.map((l, i) => l.kind !== kind ? null : (
                <tr key={l.id}>
                    <td className="pr-3 py-0.5"><Input className="h-8 text-[12px]" value={l.label} placeholder={kind === "receipt" ? "e.g. Customer receipts" : "e.g. Payroll"} onChange={e => setLine(i, { label: e.target.value })} /></td>
                    {l.amounts.map((a, w) => <td key={w} className="px-0.5 py-0.5"><Input className={cell} inputMode="decimal" value={a} onChange={e => setCell(i, w, e.target.value)} /></td>)}
                    <td><Button variant="ghost" size="sm" onClick={() => onDelete(i)} title="Remove line"><Trash2 className="w-3.5 h-3.5 text-[#94A3B8]" /></Button></td>
                </tr>
            ))}
            <tr><td colSpan={WEEKS + 2}><Button variant="ghost" size="sm" className="text-[12px]" onClick={onAdd}><Plus className="w-3 h-3 mr-1" />Add {kind} line</Button></td></tr>
        </>
    );
}

export default function CashFlowBuilder() {
    const { id } = useParams();
    return id ? <Editor key={id} id={id} /> : <List />;
}
